import { useEffect, useRef } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  useGetChatMessagesQuery,
  useMarkConversationReadMutation,
} from "../../../../app/communicationApiSlice";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/authSlice";
import UserAvatar from "../../../common/UserAvatar";
import MessageComposer from "./MessageComposer";
import PostMediaGallery from "../feed/PostMediaGallery";

function timeStr(value) {
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function participantNames(conv) {
  return (conv?.participants || [])
    .filter((p) => !p.roles?.includes("student"))
    .map((p) => p.name)
    .filter(Boolean)
    .join(", ") || "Conversation";
}

function participantPhoto(conv) {
  const p = (conv?.participants || []).find((p) => !p.roles?.includes("student"));
  return p?.photo || null;
}

export default function ChatThread({ conversationId, onBack }) {
  const user = useSelector(selectCurrentUser);
  const bottomRef = useRef(null);

  const { data, isFetching, refetch } = useGetChatMessagesQuery(
    { conversationId },
    { skip: !conversationId, pollingInterval: 15000 }
  );
  const [markRead] = useMarkConversationReadMutation();

  const messages = data?.messages || [];
  const conv = data?.conversation;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Mark as read when thread opens
  useEffect(() => {
    if (conversationId) {
      markRead(conversationId).catch(() => {});
    }
  }, [conversationId, markRead]);

  if (!conversationId) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography fontSize="3rem">💬</Typography>
        <Typography fontWeight={600}>Select a conversation</Typography>
        <Typography variant="body2">Or start a new one using the + button</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Thread header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {onBack && (
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <UserAvatar
          src={participantPhoto(conv)}
          name={participantNames(conv)}
          sx={{ width: 36, height: 36, fontSize: "0.875rem" }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {participantNames(conv)}
          </Typography>
          {conv?.student?.name && (
            <Typography variant="caption" color="text.secondary">
              re: {conv.student.name}
              {conv.classroom?.name ? ` · ${conv.classroom.name}` : ""}
            </Typography>
          )}
        </Box>
        {isFetching && <CircularProgress size={16} />}
      </Box>

      {/* Message list */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        {messages.length === 0 && !isFetching && (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography variant="body2">No messages yet. Say hello!</Typography>
          </Box>
        )}
        <Stack spacing={1.5}>
          {messages.map((msg, i) => {
            const isOwn = msg.sender?.id === user?.id;
            const showDate =
              i === 0 ||
              new Date(messages[i - 1].created_at).toDateString() !==
                new Date(msg.created_at).toDateString();

            return (
              <Box key={msg.id}>
                {showDate && (
                  <Box sx={{ textAlign: "center", my: 1 }}>
                    <Chip
                      label={new Date(msg.created_at).toLocaleDateString(undefined, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}
                      size="small"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isOwn ? "row-reverse" : "row",
                    gap: 1,
                    alignItems: "flex-end",
                  }}
                >
                  {!isOwn && (
                    <UserAvatar
                      src={msg.sender?.photo}
                      name={msg.sender?.name || "?"}
                      sx={{ width: 28, height: 28, flexShrink: 0, mb: 0.25, fontSize: "0.65rem" }}
                    />
                  )}
                  <Box
                    sx={{
                      maxWidth: "72%",
                      px: 1.75,
                      py: 1,
                      borderRadius: isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      bgcolor: isOwn ? "primary.main" : "action.hover",
                      color: isOwn ? "primary.contrastText" : "text.primary",
                    }}
                  >
                    {!isOwn && (
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        display="block"
                        sx={{ mb: 0.25, opacity: 0.85 }}
                      >
                        {msg.sender?.name}
                      </Typography>
                    )}
                    {msg.body && (
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {msg.body}
                      </Typography>
                    )}
                    {msg.data?.media_urls?.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        <PostMediaGallery data={msg.data} />
                      </Box>
                    )}
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        textAlign: isOwn ? "right" : "left",
                        mt: 0.5,
                        opacity: 0.65,
                        fontSize: "0.65rem",
                      }}
                    >
                      {timeStr(msg.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
          <Box ref={bottomRef} />
        </Stack>
      </Box>

      {/* Composer */}
      <MessageComposer conversationId={conversationId} onSent={refetch} />
    </Box>
  );
}
